import { useEffect, useState } from 'react';
import { groupZones } from '@campus/core';
import { API_BASE } from '../../lib/apiBase.js';
import { Badge, Button, Input, Modal, Select, Switch } from '../ds/index.js';
import { apiHeaders } from '../people/membersStore.js';

/**
 * Admin › Membres › Accès (tasks/acces-copycampers-plan.md) : profils d'accès
 * qui limitent certains membres à quelques cartes (ex. « Copycampers » : le
 * bâtiment Copy Camp), avec leur propre carte d'arrivée et leur propre chat.
 * Le profil se donne à chaque membre dans l'onglet Membres.
 *
 * Deux sortes : « Limite » (le membre n'entre que dans ces lieux, un seul par
 * membre) et « Accès en plus » (ces lieux sont réservés à ceux qui l'ont,
 * ex. Ascension ; un membre peut en avoir plusieurs).
 */

export type AccessProfileKind = 'limit' | 'extra';

export interface AccessProfile {
  id: string;
  kind: AccessProfileKind;
  name: string;
  zoneSlugs: string[];
  /** Salles réservées (accès en plus). */
  roomIds: string[];
  arrivalSlug: string | null;
  memberCount: number;
}

interface ZoneEntry { slug: string; displayName: string; isArrival?: boolean }
interface RoomEntry { id: string; name: string; mapSlug: string; type: string }

const ERRORS: Record<string, string> = {
  INVALID_NAME: 'Donne un nom au profil (40 caractères au plus).',
  NO_ZONE: 'Choisis au moins un lieu.',
  UNKNOWN_ROOM: 'Une des salles n’existe plus. Recharge la page.',
  INVALID_ARRIVAL: 'La carte d’arrivée doit faire partie des lieux choisis.',
  UNKNOWN_ZONE: 'Un des lieux n’existe plus. Recharge la page.',
  NAME_TAKEN: 'Un profil porte déjà ce nom.',
  PROFILE_NOT_FOUND: 'Ce profil n’existe plus.',
  KIND_LOCKED: 'Le type d’un profil ne change pas après sa création.',
};

const KIND_OPTIONS = [
  { value: 'limit', label: 'Limite : le membre n’entre que dans ces lieux' },
  { value: 'extra', label: 'Accès en plus : ces lieux sont réservés à ceux qui l’ont' },
];

export async function fetchAccessProfiles(): Promise<AccessProfile[]> {
  const res = await fetch(`${API_BASE}/admin/access-profiles`, { headers: apiHeaders() });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<AccessProfile[]>;
}

function ProfileForm({ profile, zones, rooms, onClose, onSaved }: {
  profile: AccessProfile | null;
  zones: ZoneEntry[];
  rooms: RoomEntry[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [kind, setKind] = useState<AccessProfileKind>(profile?.kind ?? 'limit');
  const [name, setName] = useState(profile?.name ?? '');
  const [slugs, setSlugs] = useState<string[]>(profile?.zoneSlugs ?? []);
  const [arrival, setArrival] = useState(profile?.arrivalSlug ?? '');
  const [roomIds, setRoomIds] = useState<string[]>(profile?.roomIds ?? []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chosen = zones.filter((z) => slugs.includes(z.slug));
  const toggle = (slug: string, on: boolean) => setSlugs((prev) => (on ? [...prev, slug] : prev.filter((s) => s !== slug)));
  const toggleRoom = (id: string, on: boolean) => setRoomIds((prev) => (on ? [...prev, id] : prev.filter((r) => r !== id)));
  // Salles regroupées par carte, dans l'ordre des cartes (campus puis bâtiments).
  const roomsByMap = zones
    .map((z) => ({ zone: z, rooms: rooms.filter((r) => r.mapSlug === z.slug) }))
    .filter((g) => g.rooms.length > 0);
  const hasChoice = slugs.length > 0 || (kind === 'extra' && roomIds.length > 0);
  const arrivalSlug = slugs.includes(arrival) ? arrival : (chosen[0]?.slug ?? '');

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/access-profiles${profile ? `/${profile.id}` : ''}`, {
        method: profile ? 'PATCH' : 'POST',
        headers: apiHeaders(true),
        body: JSON.stringify({ kind, name, zoneSlugs: slugs, roomIds: kind === 'extra' ? roomIds : [], arrivalSlug: kind === 'limit' ? arrivalSlug || null : null }),
      });
      if (!res.ok) {
        const { error: code } = await res.json().catch(() => ({ error: '' })) as { error?: string };
        setError(ERRORS[code ?? ''] ?? 'L’enregistrement n’a pas abouti.');
        return;
      }
      onSaved();
    } catch {
      setError('Erreur réseau. Réessaie dans un instant.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      title={profile ? `Profil · ${profile.name}` : 'Nouveau profil d’accès'}
      onClose={onClose}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button variant="primary" onClick={() => void save()} disabled={saving || !name.trim() || !hasChoice}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </>
      )}
    >
      <form onSubmit={(e) => { e.preventDefault(); void save(); }} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Input label="Nom" value={name} onChange={(e) => setName(e.target.value)} placeholder={kind === 'extra' ? 'Ascension' : 'Copycampers'} maxLength={40} autoFocus />
        {profile ? (
          <p className="pl-help" style={{ margin: 0 }}>Type : {kind === 'extra' ? 'accès en plus' : 'limite'}</p>
        ) : (
          <Select label="Type" value={kind} options={KIND_OPTIONS} onChange={(e) => setKind(e.target.value as AccessProfileKind)} />
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span className="pl-field-label">{kind === 'extra' ? 'Cartes réservées' : 'Lieux autorisés'}</span>
          {/* Le campus en haut, puis un groupe par bâtiment (zoneOrder.ts). */}
          {groupZones(zones).map((group) => (
            <div key={group.title} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span className="pl-help" style={{ margin: '6px 0 0', fontWeight: 600 }}>{group.title}</span>
              {group.zones.map((z) => (
                <Switch key={z.slug} checked={slugs.includes(z.slug)} onChange={(on) => toggle(z.slug, on)} label={z.displayName} />
              ))}
            </div>
          ))}
        </div>
        {kind === 'extra' && roomsByMap.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span className="pl-field-label">Salles réservées</span>
            <p className="pl-help" style={{ margin: 0 }}>
              Une salle précise, sans fermer toute la carte : ex. la salle Ascension Ads du bâtiment Mentorats.
            </p>
            {roomsByMap.map(({ zone, rooms: list }) => (
              <div key={zone.slug} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span className="pl-help" style={{ margin: '6px 0 0', fontWeight: 600 }}>{zone.displayName}</span>
                {list.map((r) => (
                  <Switch key={r.id} checked={roomIds.includes(r.id)} onChange={(on) => toggleRoom(r.id, on)} label={r.name} />
                ))}
              </div>
            ))}
          </div>
        )}
        {kind === 'limit' && chosen.length > 0 && (
          <Select
            label="Carte d’arrivée"
            value={arrivalSlug}
            options={chosen.map((z) => ({ value: z.slug, label: z.displayName }))}
            onChange={(e) => setArrival(e.target.value)}
          />
        )}
        {kind === 'extra' ? (
          <p className="pl-help" style={{ margin: 0 }}>
            Ces cartes et ces salles sont fermées aux membres et aux invités qui n’ont pas cet accès. Les admins et propriétaires y entrent toujours.
            Créer cet accès ou changer ses cartes ou ses salles déconnecte tous les membres pour en tenir compte.
          </p>
        ) : (
          <p className="pl-help" style={{ margin: 0 }}>
            Les membres de ce profil n’entrent que dans ces lieux, ont leur propre chat et doivent demander à se connecter pour écrire aux autres membres.
            {profile && profile.memberCount > 0 && ' Si tu changes les lieux, ses membres seront déconnectés pour en tenir compte.'}
          </p>
        )}
        {error && <p role="alert" style={{ margin: 0, color: 'var(--color-danger)', fontSize: 'var(--text-sm)' }}>{error}</p>}
      </form>
    </Modal>
  );
}

export function AdminAccessProfiles({ onChanged }: { onChanged: () => void }) {
  const [profiles, setProfiles] = useState<AccessProfile[] | null>(null);
  const [zones, setZones] = useState<ZoneEntry[]>([]);
  const [rooms, setRooms] = useState<RoomEntry[]>([]);
  const [editing, setEditing] = useState<AccessProfile | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = () => {
    fetchAccessProfiles().then(setProfiles).catch(() => setError('Impossible de charger les profils.'));
  };

  useEffect(() => {
    reload();
    fetch(`${API_BASE}/world/zones`, { headers: apiHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((list: ZoneEntry[]) => setZones(Array.isArray(list) ? list.map((z) => ({ slug: z.slug, displayName: z.displayName, isArrival: z.isArrival === true })) : []))
      .catch(() => setZones([]));
    fetch(`${API_BASE}/admin/access-profiles/rooms`, { headers: apiHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((list: RoomEntry[]) => setRooms(Array.isArray(list) ? list : []))
      .catch(() => setRooms([]));
  }, []);

  const zoneName = (slug: string) => zones.find((z) => z.slug === slug)?.displayName ?? slug;
  const roomName = (id: string) => rooms.find((r) => r.id === id)?.name ?? 'Salle supprimée';

  async function remove(p: AccessProfile) {
    const who = p.kind === 'extra'
      ? '\n\nCes lieux seront ouverts à tous les membres, qui seront déconnectés pour en tenir compte.'
      : p.memberCount > 0 ? `\n\nSes ${p.memberCount} membre${p.memberCount > 1 ? 's' : ''} retrouveront tout le campus et seront déconnectés.` : '';
    if (!window.confirm(`Supprimer le profil « ${p.name} » ?${who}`)) return;
    const res = await fetch(`${API_BASE}/admin/access-profiles/${p.id}`, { method: 'DELETE', headers: apiHeaders() }).catch(() => null);
    if (!res?.ok) { setError('La suppression n’a pas abouti.'); return; }
    reload();
    onChanged();
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <p className="pl-page-sub" style={{ margin: 0 }}>
          Un profil « Limite » réduit ses membres à quelques lieux, avec leur propre carte d’arrivée et leur propre chat. Un « Accès en plus » réserve des lieux à ceux qui l’ont. Les admins et propriétaires gardent tout le campus.
        </p>
        <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>Nouveau profil</Button>
      </div>
      {error && (
        <div className="pl-alert" role="alert" style={{ justifyContent: 'space-between' }}>
          <span>{error}</span>
          <Button variant="ghost" size="sm" icon="x" aria-label="Fermer" onClick={() => setError(null)} />
        </div>
      )}
      {profiles && (
        <div className="pl-panel" style={{ overflow: 'hidden' }}>
          <table className="pl-table">
            <thead>
              <tr><th>Profil</th><th>Type</th><th>Lieux</th><th>Arrivée</th><th>Membres</th><th><span className="pl-sr">Actions</span></th></tr>
            </thead>
            <tbody>
              {profiles.length === 0 && (
                <tr><td colSpan={6} className="pl-muted" style={{ textAlign: 'center', padding: 24 }}>Aucun profil : tous les membres ont accès à tout le campus.</td></tr>
              )}
              {profiles.map((p) => (
                <tr key={p.id}>
                  <td style={{ fontWeight: 500 }}>{p.name}</td>
                  <td>{p.kind === 'extra' ? <Badge tone="primary">Accès en plus</Badge> : <Badge>Limite</Badge>}</td>
                  <td>
                    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
                      {p.zoneSlugs.map((s) => <Badge key={s}>{zoneName(s)}</Badge>)}
                      {(p.roomIds ?? []).map((id) => <Badge key={id} tone="primary">Salle {roomName(id)}</Badge>)}
                    </span>
                  </td>
                  <td className="pl-muted">{p.arrivalSlug ? zoneName(p.arrivalSlug) : '-'}</td>
                  <td className="pl-num">{p.memberCount}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <span style={{ display: 'inline-flex', gap: 6 }}>
                      <Button variant="ghost" size="sm" onClick={() => setEditing(p)}>Modifier</Button>
                      <Button variant="danger" size="sm" onClick={() => void remove(p)}>Supprimer</Button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <ProfileForm
          profile={editing === 'new' ? null : editing}
          zones={zones}
          rooms={rooms}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); onChanged(); }}
        />
      )}
    </>
  );
}
